import React, { useEffect, useState } from "react";
import {
  Link2, ShieldCheck, AlertTriangle, Activity, GitBranch,
  TrendingUp, DollarSign, MessageSquare, Gauge, FileSearch, Cpu, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";

/**
 * CamelionAI Bridge — unified clinical-financial layer connecting
 * ZidaMD ⇄ ProsthoAI ⇄ ReimbursePro.
 *
 * This page demonstrates all 8 Bridge connections live against synthetic data.
 * Each connection card shows:
 *   • Title + description
 *   • A demo trigger that calls the real Bridge endpoint
 *   • The actual response data
 *   • A "Synthetic data" badge so users know they're seeing simulated cross-product feeds
 */

const PLATFORM_COLORS = {
  ZidaMD: "bg-brand-primary text-white",
  ProsthoAI: "bg-emerald-600 text-white",
  ReimbursePro: "bg-amber-600 text-white",
};

const BAND_STYLES = {
  excellent: "text-emerald-700 bg-emerald-50 border-emerald-200",
  strong: "text-emerald-700 bg-emerald-50 border-emerald-200",
  fair: "text-amber-700 bg-amber-50 border-amber-200",
  "needs-attention": "text-red-700 bg-red-50 border-red-200",
  ready: "text-emerald-700 bg-emerald-50 border-emerald-200",
  "needs-review": "text-amber-700 bg-amber-50 border-amber-200",
  incomplete: "text-red-700 bg-red-50 border-red-200",
};

const SyntheticBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium" data-testid="synthetic-badge">
    <Cpu className="w-3 h-3" /> Synthetic data · beta
  </span>
);

const PlatformTag = ({ name }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${PLATFORM_COLORS[name] || "bg-brand-bg text-brand-text"}`}>
    {name}
  </span>
);

const BridgeCard = ({ id, icon: Icon, title, description, testid, children }) => (
  <div className="border border-brand rounded-lg p-5 bg-white" data-testid={`bridge-card-${id}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className="w-9 h-9 rounded-md bg-brand-bg flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-brand-muted">Bridge connection {id}</div>
        <h3 className="text-base font-semibold text-brand-text">{title}</h3>
        <p className="text-xs text-brand-muted mt-1">{description}</p>
      </div>
      <SyntheticBadge />
    </div>
    <div data-testid={`bridge-card-content-${id}`}>{children}</div>
  </div>
);

export default function CamelionBridge() {
  const [status, setStatus] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // F1
  const [claim, setClaim] = useState(null);
  const [busy1, setBusy1] = useState(false);
  // F2
  const [patterns, setPatterns] = useState(null);
  const [busy2, setBusy2] = useState(false);
  // F3
  const [timeline, setTimeline] = useState(null);
  const [busy3, setBusy3] = useState(false);
  // F4
  const [paCheck, setPACheck] = useState(null);
  const [busy4, setBusy4] = useState(false);
  // F5
  const [revenue, setRevenue] = useState(null);
  const [busy5, setBusy5] = useState(false);
  // F6
  const [health, setHealth] = useState(null);
  // F7
  const [comms, setComms] = useState(null);
  const [busy7, setBusy7] = useState(false);
  // F8
  const [noteSample, setNoteSample] = useState("Pt seen today for f/u DM2 — A1c improved 6.9%; continuing metformin 1000mg BID.");
  const [docScore, setDocScore] = useState(null);
  const [busy8, setBusy8] = useState(false);

  useEffect(() => { boot(); }, []);

  const boot = async () => {
    try {
      const [s, p, h] = await Promise.all([
        api.get("/bridge/status"),
        api.get("/bridge/patients"),
        api.get("/bridge/practice-health-score"),
      ]);
      setStatus(s.data);
      setPatients(p.data.patients);
      setSelectedPatient(p.data.patients[0]);
      setHealth(h.data);
    } catch (err) {
      toast.error("Could not load Bridge status");
    }
  };

  // 1. Clinical → Billing
  const runClinicalToBilling = async () => {
    if (!selectedPatient) return;
    setBusy1(true);
    try {
      const r = await api.post("/bridge/clinical-to-billing", {
        patient_token: selectedPatient.token,
        payer: selectedPatient.primary_payer,
        diagnosis_codes: ["M54.5", "M62.830"],
        procedure_codes: ["99214", "70551"],
      });
      setClaim(r.data.claim_draft);
      toast.success(`Claim draft ${r.data.claim_draft.claim_id} created in ReimbursePro`);
    } catch (err) { toast.error("Bridge call failed"); }
    finally { setBusy1(false); }
  };

  // 2. Denial prevention
  const runDenialPrevention = async () => {
    setBusy2(true);
    try { const r = await api.get("/bridge/denial-prevention"); setPatterns(r.data); }
    catch { toast.error("Bridge call failed"); }
    finally { setBusy2(false); }
  };

  // 3. Unified timeline
  const runTimeline = async () => {
    if (!selectedPatient) return;
    setBusy3(true);
    try { const r = await api.get(`/bridge/unified-timeline?patient_token=${selectedPatient.token}`); setTimeline(r.data); }
    catch { toast.error("Bridge call failed"); }
    finally { setBusy3(false); }
  };

  // 4. PA pre-check
  const runPAPrecheck = async () => {
    if (!selectedPatient) return;
    setBusy4(true);
    try {
      const r = await api.post("/bridge/prior-auth-precheck", {
        payer: selectedPatient.primary_payer,
        procedure_codes: ["99214", "70551"],
      });
      setPACheck(r.data);
    } catch { toast.error("Bridge call failed"); }
    finally { setBusy4(false); }
  };

  // 5. Revenue impact
  const runRevenue = async () => {
    if (!selectedPatient) return;
    setBusy5(true);
    try {
      const r = await api.post("/bridge/revenue-impact", {
        payer: selectedPatient.primary_payer,
        procedure_codes: ["99214", "70551"],
      });
      setRevenue(r.data);
    } catch { toast.error("Bridge call failed"); }
    finally { setBusy5(false); }
  };

  // 7. Unified comms
  const runComms = async () => {
    if (!selectedPatient) return;
    setBusy7(true);
    try { const r = await api.get(`/bridge/unified-comms?patient_token=${selectedPatient.token}`); setComms(r.data); }
    catch { toast.error("Bridge call failed"); }
    finally { setBusy7(false); }
  };

  // 8. Documentation scoring
  const runDocScore = async () => {
    setBusy8(true);
    try {
      const r = await api.post("/bridge/documentation-score", {
        note_text: noteSample,
        cpt_codes: ["99214"],
        icd10_codes: ["E11.9"],
      });
      setDocScore(r.data);
    } catch { toast.error("Bridge call failed"); }
    finally { setBusy8(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="CamelionAI Bridge · v0.1.0-beta"
        title="Unified Clinical-Financial Layer"
        subtitle="Connects ZidaMD ⇄ ProsthoAI ⇄ ReimbursePro. De-identified — no PHI crosses the Bridge during beta."
        testid="bridge-header"
      />

      {/* Status header */}
      {status && (
        <div className="border border-brand rounded-xl p-5 bg-gradient-to-br from-brand-bg to-white mb-6" data-testid="bridge-status">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-brand-primary" />
            <div className="font-semibold text-sm text-brand-text">Bridge mode: <span className="uppercase text-brand-primary">{status.mode}</span></div>
            <span className="ml-auto text-[10px] text-brand-muted">{status.version}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {status.platforms.map(p => (
              <div key={p.name} className="flex items-center gap-2 p-3 border border-brand rounded-md bg-white" data-testid={`platform-${p.name}`}>
                <span className={`w-2 h-2 rounded-full ${p.status === "connected" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <PlatformTag name={p.name} />
                <span className="text-[10px] text-brand-muted ml-auto">{p.data_source}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-start gap-2 text-[11px] text-brand-muted">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-primary" />
            <span>{status.phi_policy}</span>
          </div>
        </div>
      )}

      {/* Patient selector (single dropdown drives connections 1, 3, 4, 5, 7) */}
      <div className="border border-brand rounded-md p-3 mb-6 bg-white flex items-center gap-3 flex-wrap" data-testid="bridge-patient-selector">
        <span className="text-xs text-brand-muted">De-identified patient cohort:</span>
        {patients.map(p => (
          <button
            key={p.token}
            onClick={() => setSelectedPatient(p)}
            className={`text-xs px-3 py-1.5 rounded-md border ${selectedPatient?.token === p.token ? "border-brand-primary bg-brand-bg" : "border-brand bg-white hover:bg-brand-bg/50"}`}
            data-testid={`patient-${p.token}`}
          >
            <span className="font-mono text-[10px] text-brand-muted">{p.token.slice(-6)}</span>
            <span className="mx-1 text-brand-muted">·</span>
            <span>{p.age_band} {p.sex} · {p.primary_payer}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* 1. Clinical → Billing */}
        <BridgeCard id={1} icon={GitBranch} title="Clinical → Billing Auto-Bridge" description="Treatment plans auto-generate claim drafts in ReimbursePro." testid="conn-1">
          <button onClick={runClinicalToBilling} disabled={busy1 || !selectedPatient} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-1">
            {busy1 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 inline mr-1.5" />}
            Generate claim draft
          </button>
          {claim && (
            <div className="mt-3 p-3 border border-brand rounded-md bg-brand-bg/40 text-xs" data-testid="result-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-brand-primary font-semibold">{claim.claim_id}</span>
                <PlatformTag name="ReimbursePro" />
                <span className="ml-auto text-emerald-700 font-semibold">${claim.expected_reimbursement.toFixed(2)}</span>
              </div>
              <div className="text-brand-muted">{claim.diagnosis_codes.join(", ")} → {claim.procedure_codes.join(", ")}</div>
              <div className="text-[10px] text-brand-muted mt-1">Status: {claim.status}</div>
            </div>
          )}
        </BridgeCard>

        {/* 2. Denial prevention */}
        <BridgeCard id={2} icon={AlertTriangle} title="Denial Prevention Intelligence" description="Live denial patterns from ReimbursePro fed back as pre-doc alerts in ZidaMD." testid="conn-2">
          <button onClick={runDenialPrevention} disabled={busy2} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-2">
            {busy2 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <FileSearch className="w-3.5 h-3.5 inline mr-1.5" />}
            Load denial patterns
          </button>
          {patterns && (
            <div className="mt-3 space-y-2" data-testid="result-2">
              <div className="text-xs text-brand-text">Total preventable loss (90d): <b className="text-red-700">${patterns.total_loss_last_90d.toLocaleString()}</b></div>
              {patterns.patterns.slice(0, 3).map(p => (
                <div key={p.code} className="p-2 border border-brand rounded-md bg-white text-[11px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-semibold text-brand-primary">{p.code}</span>
                    <span className="text-brand-text">{p.label}</span>
                    <span className="ml-auto text-red-700 font-semibold">{p.freq_last_90d}× · ${p.avg_loss_per_denial}/ea</span>
                  </div>
                  <div className="text-brand-muted"><b>Prevent:</b> {p.prevention}</div>
                </div>
              ))}
            </div>
          )}
        </BridgeCard>

        {/* 3. Unified timeline */}
        <BridgeCard id={3} icon={Activity} title="Unified Patient Timeline" description="Chronological view of one patient across all three platforms." testid="conn-3">
          <button onClick={runTimeline} disabled={busy3 || !selectedPatient} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-3">
            {busy3 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <Activity className="w-3.5 h-3.5 inline mr-1.5" />}
            Load timeline
          </button>
          {timeline && (
            <div className="mt-3 space-y-1.5 text-[11px]" data-testid="result-3">
              {timeline.events.map((e, i) => (
                <div key={i} className="flex items-start gap-2 p-1.5 border-l-2 border-brand bg-white">
                  <span className="text-brand-muted shrink-0 w-20">{new Date(e.ts).toLocaleDateString()}</span>
                  <PlatformTag name={e.platform} />
                  <span className="flex-1 text-brand-text">{e.summary}</span>
                </div>
              ))}
            </div>
          )}
        </BridgeCard>

        {/* 4. PA pre-check */}
        <BridgeCard id={4} icon={ShieldCheck} title="Prior Authorization Pre-Check" description="Auth alerts surfaced in the clinical platform before the treatment plan is finalized." testid="conn-4">
          <button onClick={runPAPrecheck} disabled={busy4 || !selectedPatient} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-4">
            {busy4 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5" />}
            Run PA pre-check
          </button>
          {paCheck && (
            <div className="mt-3 space-y-2 text-xs" data-testid="result-4">
              <div className={`p-2 rounded-md border ${paCheck.any_pa_required ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
                {paCheck.message}
              </div>
              {paCheck.checks.map(c => (
                <div key={c.cpt} className="flex items-center gap-2 p-2 border border-brand rounded-md bg-white text-[11px]">
                  <span className="font-mono font-semibold">{c.cpt}</span>
                  <span className={c.required ? "text-amber-700 font-semibold" : "text-emerald-700"}>{c.required ? "PA REQUIRED" : "no PA"}</span>
                  {c.required && <span className="ml-auto text-brand-muted">{c.median_turnaround_hours}h turnaround</span>}
                </div>
              ))}
            </div>
          )}
        </BridgeCard>

        {/* 5. Revenue impact */}
        <BridgeCard id={5} icon={DollarSign} title="Revenue Impact Dashboard" description="Expected reimbursement displayed at the point of care." testid="conn-5">
          <button onClick={runRevenue} disabled={busy5 || !selectedPatient} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-5">
            {busy5 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <DollarSign className="w-3.5 h-3.5 inline mr-1.5" />}
            Estimate this encounter
          </button>
          {revenue && (
            <div className="mt-3" data-testid="result-5">
              <div className="text-2xl font-semibold text-emerald-700">${revenue.total_expected.toFixed(2)}</div>
              <div className="text-[11px] text-brand-muted mb-2">Expected from {revenue.payer}</div>
              <div className="space-y-1">
                {revenue.line_items.map(li => (
                  <div key={li.cpt} className="flex items-center justify-between text-[11px] p-1.5 border border-brand rounded">
                    <span className="font-mono">{li.cpt}</span>
                    <span className={li.in_schedule ? "text-emerald-700" : "text-brand-muted"}>{li.expected ? `$${li.expected.toFixed(2)}` : "not in fee schedule"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </BridgeCard>

        {/* 6. Practice health score */}
        <BridgeCard id={6} icon={Gauge} title="Practice Health Score" description="Unified 1-100 score across all three platforms — clinical, case acceptance, financial." testid="conn-6">
          {health ? (
            <div data-testid="result-6">
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-4xl font-bold text-brand-primary">{health.score}</div>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${BAND_STYLES[health.band] || ""}`}>{health.band.replace("-", " ")}</div>
              </div>
              <div className="space-y-1.5">
                {Object.entries(health.components).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-[11px] text-brand-muted mb-0.5"><span className="capitalize">{k.replace(/_/g, " ")}</span><span>{v}</span></div>
                    <div className="h-1.5 bg-brand-bg rounded-full overflow-hidden">
                      <div className="h-full bg-brand-primary" style={{ width: `${v}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-xs text-brand-muted">Loading…</div>}
        </BridgeCard>

        {/* 7. Unified comms */}
        <BridgeCard id={7} icon={MessageSquare} title="Unified Patient Communication" description="One consistent communication thread across ZidaMD and ReimbursePro." testid="conn-7">
          <button onClick={runComms} disabled={busy7 || !selectedPatient} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-7">
            {busy7 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />}
            Load comm thread
          </button>
          {comms && (
            <div className="mt-3 space-y-1.5 text-[11px]" data-testid="result-7">
              {comms.thread.map((m, i) => (
                <div key={i} className="flex items-start gap-2 p-2 border border-brand rounded bg-white">
                  <span className="text-brand-muted shrink-0 w-16">{new Date(m.ts).toLocaleDateString()}</span>
                  <PlatformTag name={m.platform} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-text">{m.from} · <span className="text-brand-muted font-normal">{m.channel}</span></div>
                    <div className="text-brand-muted line-clamp-1">{m.preview}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BridgeCard>

        {/* 8. Documentation scoring */}
        <BridgeCard id={8} icon={TrendingUp} title="Documentation Quality Scoring" description="ReimbursePro scores clinical notes against billing requirements in real time." testid="conn-8">
          <textarea
            value={noteSample}
            onChange={(e) => setNoteSample(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-brand rounded-md text-xs mb-2"
            placeholder="Paste a clinical note…"
            data-testid="docscore-input"
          />
          <button onClick={runDocScore} disabled={busy8 || !noteSample.trim()} className="px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="run-8">
            {busy8 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> : <TrendingUp className="w-3.5 h-3.5 inline mr-1.5" />}
            Score this note
          </button>
          {docScore && (
            <div className="mt-3" data-testid="result-8">
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-3xl font-bold text-brand-primary">{docScore.score}</div>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${BAND_STYLES[docScore.band] || ""}`}>{docScore.band.replace("-", " ")}</div>
                <span className="ml-auto text-[10px] text-brand-muted">Supports {docScore.supports_em_level}</span>
              </div>
              <div className="text-[11px] text-brand-text mb-2">Predicted denial risk: <b className={docScore.predicted_denial_risk === "low" ? "text-emerald-700" : docScore.predicted_denial_risk === "moderate" ? "text-amber-700" : "text-red-700"}>{docScore.predicted_denial_risk}</b></div>
              {docScore.flags.length > 0 && (
                <ul className="text-[11px] space-y-1">
                  {docScore.flags.map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${f.severity === "high" ? "bg-red-500" : f.severity === "medium" ? "bg-amber-500" : "bg-brand-muted"}`} />
                      <span className="text-brand-muted">{f.issue}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </BridgeCard>

      </div>

      <div className="mt-8 p-4 border border-brand rounded-md bg-brand-bg/40 text-[11px] text-brand-muted" data-testid="bridge-footer">
        <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 text-brand-primary" />
        <b className="text-brand-text">Beta architecture notice:</b> CamelionAI Bridge is currently in synthetic-data mode. All patient tokens are SHA-256 one-way hashes. No raw PHI traverses the Bridge until the inter-product BAA and HIPAA architecture review are complete.
      </div>
    </>
  );
}
